import { computed, inject, Injectable, Injector } from '@angular/core';

import {
  isSchemaReference,
  loadedState,
  resolveSchemaPart,
  type SchemaModalDeclaration,
  type SchemaModalHost,
  type SchemaNode,
  SchemaNodeOutletComponent,
  schemaPartInjector,
  type SchemaReference,
} from '@kros-sk/core/schema';
import { type KrosModalRef, KrosModalService } from '@kros-sk/ui/modal';

import { UrlModals } from './url-modals.service';

/**
 * The URL-bound modal host. Declares each modal a schema node declares with the engine, under its id and `inUrl`, and
 * opens it in the modal shell — the declared `modal-layout` draws and sizes the window — under the declaring node's
 * injector, with the shell settings the entry's own keys decide: `closeOnBackdropClick`, and `phone: "drawer"` as a
 * sheet on a phone (`sheetOnPhone`). The shell renders the declared node on its params, which arrive as text and become
 * the node's incoming state, so its addresses read them as `$data`, anew whenever they change. Provided by
 * `provideUrlModals()`.
 *
 * An entry that stands for another document and declares no `$refStates.loading` opens once its part has arrived, with
 * the part, under an injector that knows the part, so a reference back to it inside fails as a cycle. Its
 * `$refStates.error` shows if the part fails, and without one nothing opens (672307).
 */
@Injectable()
export class UrlSchemaModalHost implements SchemaModalHost {
  readonly #urlModals = inject(UrlModals);

  readonly #modalService = inject(KrosModalService);

  declare(modalId: string, declaration: SchemaModalDeclaration, injector: Injector): () => void {
    const { closeOnBackdropClick, inUrl, phone, ...node } = declaration;
    const reference = waitingReference(node);
    const windowInjector = reference ? schemaPartInjector(reference, injector) : injector;
    let part: SchemaNode | undefined;

    return this.#urlModals.declare(modalId, {
      inUrl: inUrl ?? 'never',
      ...(reference
        ? {
            prepare: async (abortSignal: AbortSignal): Promise<void> => {
              const resolved = await resolveSchemaPart(reference, injector, abortSignal);

              if (!abortSignal.aborted) {
                part = resolved;
              }
            },
          }
        : {}),
      opener: (params): KrosModalRef => {
        const shown = part ?? node;

        return this.#modalService.openCenteredShell(SchemaNodeOutletComponent, undefined, {
          injector: windowInjector,
          contentInputs: computed(() => ({ node: shown, state: loadedState(params()) })),
          ...(closeOnBackdropClick === undefined ? {} : { closeOnBackdropClick }),
          ...(phone === 'drawer' ? { sheetOnPhone: true } : {}),
        });
      },
    });
  }
}

/** The entry when it stands for another document and declares no loading placeholder: its window waits for the part. */
function waitingReference(node: object): SchemaReference | null {
  return isSchemaReference(node) && !node.$refStates?.loading ? node : null;
}
